import supabase from '../lib/supabase';
import { addDays, differenceInDays, format } from 'date-fns';

// Demo data for offline mode
const demoPayments = [
  {
    id: 1,
    invoice_id: 1,
    invoice_number: 'INV-1001',
    client_id: 1,
    client: {
      name: 'John Smith',
      business_name: 'TechCorp Ltd',
      email: 'john@techcorp.com'
    },
    amount: 4130.00,
    paid_amount: 4130.00,
    remaining_balance: 0.00,
    payment_status: 'paid',
    payment_method: 'bank_transfer',
    payment_date: '2024-01-20',
    transaction_id: 'TXN-001-20240120',
    late_fee: 0.00,
    payment_notes: 'Payment received on time',
    receipt_number: 'RCP-001-20240120',
    created_at: '2024-01-20T00:00:00Z',
    updated_at: '2024-01-20T00:00:00Z'
  },
  {
    id: 2,
    invoice_id: 2,
    invoice_number: 'INV-1002',
    client_id: 2,
    client: {
      name: 'Michael Brown',
      business_name: 'StartupXYZ',
      email: 'mike@startupxyz.com'
    },
    amount: 9440.00,
    paid_amount: 5000.00,
    remaining_balance: 4440.00,
    payment_status: 'partial',
    payment_method: 'credit_card',
    payment_date: '2024-01-25',
    transaction_id: 'TXN-002-20240125',
    late_fee: 0.00,
    payment_notes: 'Partial payment received, balance pending',
    receipt_number: 'RCP-002-20240125',
    created_at: '2024-01-25T00:00:00Z',
    updated_at: '2024-01-25T00:00:00Z'
  },
  {
    id: 3,
    invoice_id: 3,
    invoice_number: 'INV-1003',
    client_id: 3,
    client: {
      name: 'Lisa Anderson',
      business_name: 'E-commerce Plus',
      email: 'lisa@ecommerceplus.com'
    },
    amount: 1200.00,
    paid_amount: 0.00,
    remaining_balance: 1200.00,
    payment_status: 'overdue',
    payment_method: null,
    payment_date: null,
    transaction_id: null,
    late_fee: 72.00,
    payment_notes: 'Payment overdue by 15 days',
    receipt_number: null,
    created_at: '2024-01-10T00:00:00Z',
    updated_at: '2024-02-15T00:00:00Z'
  }
];

const paymentMethods = [
  { id: 'bank_transfer', name: 'Bank Transfer', icon: 'FiCreditCard' },
  { id: 'credit_card', name: 'Credit Card', icon: 'FiCreditCard' },
  { id: 'paypal', name: 'PayPal', icon: 'FiDollarSign' },
  { id: 'stripe', name: 'Stripe', icon: 'FiCreditCard' },
  { id: 'wire_transfer', name: 'Wire Transfer', icon: 'FiSend' },
  { id: 'check', name: 'Check', icon: 'FiFileText' },
  { id: 'cash', name: 'Cash', icon: 'FiDollarSign' },
  { id: 'other', name: 'Other', icon: 'FiMoreHorizontal' }
];

class PaymentService {
  constructor() {
    this.isOnline = false;
    this.checkConnection();
  }

  async checkConnection() {
    try {
      const { data, error } = await supabase.from('payments_2024').select('count', { count: 'exact' });
      this.isOnline = !error;
    } catch (error) {
      this.isOnline = false;
    }
  }

  generateReceiptNumber() {
    const currentDate = format(new Date(), 'yyyyMMdd');
    const timestamp = Date.now().toString().slice(-4);
    return `RCP-${currentDate}-${timestamp}`;
  }

  generateTransactionId() {
    const currentDate = format(new Date(), 'yyyyMMdd');
    const timestamp = Date.now().toString().slice(-6);
    return `TXN-${timestamp}-${currentDate}`;
  }

  calculateLateFee(invoiceAmount, dueDate, lateFeePercentage = 1) {
    const today = new Date();
    const due = new Date(dueDate);
    const daysOverdue = differenceInDays(today, due);
    
    if (daysOverdue <= 0) {
      return 0;
    }

    const weeksOverdue = Math.ceil(daysOverdue / 7);
    const lateFee = (invoiceAmount * (lateFeePercentage / 100)) * weeksOverdue;
    
    return Math.round(lateFee * 100) / 100;
  }

  async getAllPayments() {
    if (!this.isOnline) {
      return { data: demoPayments, error: null };
    }

    try {
      const { data, error } = await supabase
        .from('payments_2024')
        .select(`
          *,
          invoice:invoices_2024(*),
          client:clients_billing_2024(*)
        `)
        .order('created_at', { ascending: false });

      return { data: data || [], error };
    } catch (error) {
      return { data: demoPayments, error: null };
    }
  }

  async getPayment(id) {
    if (!this.isOnline) {
      const payment = demoPayments.find(p => p.id === parseInt(id));
      return { data: payment, error: payment ? null : 'Payment not found' };
    }

    try {
      const { data, error } = await supabase
        .from('payments_2024')
        .select(`
          *,
          invoice:invoices_2024(*),
          client:clients_billing_2024(*)
        `)
        .eq('id', id)
        .single();

      return { data, error };
    } catch (error) {
      const payment = demoPayments.find(p => p.id === parseInt(id));
      return { data: payment, error: payment ? null : 'Payment not found' };
    }
  }

  async getPaymentsByInvoice(invoiceId) {
    if (!this.isOnline) {
      const payments = demoPayments.filter(p => p.invoice_id === parseInt(invoiceId));
      return { data: payments, error: null };
    }

    try {
      const { data, error } = await supabase
        .from('payments_2024')
        .select(`
          *,
          invoice:invoices_2024(*),
          client:clients_billing_2024(*)
        `)
        .eq('invoice_id', invoiceId)
        .order('created_at', { ascending: false });

      return { data: data || [], error };
    } catch (error) {
      const payments = demoPayments.filter(p => p.invoice_id === parseInt(invoiceId));
      return { data: payments, error: null };
    }
  }

  async recordPayment(paymentData) {
    const receiptNumber = this.generateReceiptNumber();
    const transactionId = paymentData.transaction_id || this.generateTransactionId();

    const newPayment = {
      ...paymentData,
      receipt_number: receiptNumber,
      transaction_id: transactionId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    if (!this.isOnline) {
      newPayment.id = Math.max(...demoPayments.map(p => p.id)) + 1;
      demoPayments.unshift(newPayment);
      return { data: newPayment, error: null };
    }

    try {
      const { data, error } = await supabase
        .from('payments_2024')
        .insert([newPayment])
        .select(`
          *,
          invoice:invoices_2024(*),
          client:clients_billing_2024(*)
        `)
        .single();

      return { data, error };
    } catch (error) {
      newPayment.id = Math.max(...demoPayments.map(p => p.id)) + 1;
      demoPayments.unshift(newPayment);
      return { data: newPayment, error: null };
    }
  }

  async updatePayment(id, paymentData) {
    const updatedData = {
      ...paymentData,
      updated_at: new Date().toISOString()
    };

    if (!this.isOnline) {
      const index = demoPayments.findIndex(p => p.id === parseInt(id));
      if (index !== -1) {
        demoPayments[index] = { ...demoPayments[index], ...updatedData };
        return { data: demoPayments[index], error: null };
      }
      return { data: null, error: 'Payment not found' };
    }

    try {
      const { data, error } = await supabase
        .from('payments_2024')
        .update(updatedData)
        .eq('id', id)
        .select(`
          *,
          invoice:invoices_2024(*),
          client:clients_billing_2024(*)
        `)
        .single();

      return { data, error };
    } catch (error) {
      const index = demoPayments.findIndex(p => p.id === parseInt(id));
      if (index !== -1) {
        demoPayments[index] = { ...demoPayments[index], ...updatedData };
        return { data: demoPayments[index], error: null };
      }
      return { data: null, error: 'Payment not found' };
    }
  }

  async deletePayment(id) {
    if (!this.isOnline) {
      const index = demoPayments.findIndex(p => p.id === parseInt(id));
      if (index !== -1) {
        const deleted = demoPayments.splice(index, 1)[0];
        return { data: deleted, error: null };
      }
      return { data: null, error: 'Payment not found' };
    }

    try {
      const { data, error } = await supabase
        .from('payments_2024')
        .delete()
        .eq('id', id)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      const index = demoPayments.findIndex(p => p.id === parseInt(id));
      if (index !== -1) {
        const deleted = demoPayments.splice(index, 1)[0];
        return { data: deleted, error: null };
      }
      return { data: null, error: 'Payment not found' };
    }
  }

  async getPaymentMethods() {
    return { data: paymentMethods, error: null };
  }

  async getPaymentsByStatus(status) {
    const { data: payments } = await this.getAllPayments();
    return {
      data: payments.filter(payment => payment.payment_status === status),
      error: null
    };
  }

  async getOverduePayments() {
    return await this.getPaymentsByStatus('overdue');
  }

  async generatePaymentReceipt(paymentId) {
    const { data: payment } = await this.getPayment(paymentId);
    if (!payment) {
      return { data: null, error: 'Payment not found' };
    }

    // In a real application, this would generate a PDF receipt
    const receiptData = {
      receipt_number: payment.receipt_number,
      payment_date: payment.payment_date,
      amount: payment.paid_amount,
      payment_method: payment.payment_method,
      transaction_id: payment.transaction_id,
      invoice_number: payment.invoice_number,
      client: payment.client
    };

    return { data: receiptData, error: null };
  }

  async sendPaymentReminder(paymentId) {
    // In a real application, this would send an email reminder
    console.log(`Sending payment reminder for payment ID: ${paymentId}`);
    return { data: { sent: true }, error: null };
  }

  async processAutomaticLateFees() {
    // This would typically run as a scheduled job
    const { data: overduePayments } = await this.getOverduePayments();
    const updatedPayments = [];

    for (const payment of overduePayments) {
      if (payment.invoice && payment.invoice.due_date) {
        const lateFee = this.calculateLateFee(
          payment.amount,
          payment.invoice.due_date,
          1 // 1% per week
        );

        if (lateFee > payment.late_fee) {
          const { data: updated } = await this.updatePayment(payment.id, {
            late_fee: lateFee,
            payment_notes: `Late fee applied: $${lateFee.toFixed(2)}`
          });
          updatedPayments.push(updated);
        }
      }
    }

    return { data: updatedPayments, error: null };
  }

  async getPaymentStatistics() {
    const { data: payments } = await this.getAllPayments();
    
    const stats = {
      total_payments: payments.length,
      total_amount_received: payments.reduce((sum, p) => sum + (p.paid_amount || 0), 0),
      total_pending: payments.filter(p => p.payment_status === 'pending').length,
      total_overdue: payments.filter(p => p.payment_status === 'overdue').length,
      total_late_fees: payments.reduce((sum, p) => sum + (p.late_fee || 0), 0),
      outstanding_balance: payments.reduce((sum, p) => sum + (p.remaining_balance || 0), 0)
    };

    return { data: stats, error: null };
  }
}

export default new PaymentService();